import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import _ from "lodash";
import { InputSingleValue, SelectOption } from "../../types";
import { twMerge } from "tailwind-merge";
import { getBasicInputClassNames } from "./getBasicInputClassNames";
import { getOptionString, getOptionValue } from "../../utils";

interface SelectProps<
    T extends InputSingleValue | InputSingleValue[] | null | undefined
> {
    value: T;
    onChange: (value: T) => void;
    options: SelectOption[];
    multiple?: boolean;
    disabled?: boolean;
    className?: string;
    buttonClassName?: string;
    optionsClassName?: string;
    valueToDisplay?: string | number | null;
    placeholder?: string;
    error?: boolean;
    isDirty?: boolean;
}

export default function Select<
    T extends InputSingleValue | InputSingleValue[] | null | undefined
>({
    value,
    onChange,
    options,
    multiple = false,
    disabled = false,
    className = "",
    buttonClassName = "",
    optionsClassName = "",
    valueToDisplay,
    placeholder,
    error = false,
    isDirty = false,
}: SelectProps<T>) {
    const mergedButtonClassName = twMerge(
        "text-start",
        getBasicInputClassNames({
            error,
            isDirty,
            disabled,
        }),
        buttonClassName
    );
    const defaultOptionsStyle =
        "w-[230px] shadow-[0px_4px_4px_0px_#00000040] ml-3 py-1.5 bg-white rounded-b-md space-y-3";

    const displayValue =
        valueToDisplay?.toString() ??
        (Array.isArray(value)
            ? value.length > 0
                ? value
                      .map((v) =>
                          options.find((opt) => getOptionValue(opt) === v)
                      )
                      .filter(Boolean)
                      .map(getOptionString)
                      .join(", ")
                : placeholder
            : value !== null && value !== undefined
            ? getOptionString(
                  options.find((opt) => getOptionValue(opt) === value)
              ) || placeholder
            : placeholder);

    return (
        <div className={className}>
            <Listbox
                value={value}
                onChange={(val) => {
                    if (multiple) {
                        const newValues = val as InputSingleValue[];
                        const currentValues = Array.isArray(value) ? value : [];
                        const diff = _.xor(newValues, currentValues);
                        if (diff.length > 0) {
                            onChange(newValues as T);
                        }
                    } else {
                        onChange(val as T);
                    }
                }}
                multiple={multiple}
            >
                <ListboxButton
                    disabled={disabled}
                    className={mergedButtonClassName}
                >
                    <span className="w-11/12 truncate">{displayValue}</span>
                    <ChevronDownIcon className="size-3.5 shrink-0 ms-1 xl:ms-2" />
                </ListboxButton>
                <ListboxOptions
                    className={twMerge(defaultOptionsStyle, optionsClassName)}
                    anchor="bottom"
                >
                    {options.map((option) => {
                        const optionString = getOptionString(option);
                        const optionValue = getOptionValue(option);
                        const isSelected = multiple
                            ? Array.isArray(value) &&
                              optionValue !== null &&
                              value.includes(optionValue)
                            : value === optionValue;

                        return (
                            <ListboxOption
                                key={optionValue}
                                value={optionValue}
                                className="h-7 xl:h-9 flex items-center pl-3 pr-1 text-black font-semibold bg-white w-full text-left data-[focus]:bg-blue-option-select cursor-pointer"
                            >
                                {multiple && (
                                    <div className="relative shrink-0 size-5 xl:size-6 mr-2 rounded-sm border-2 border-blue-medium">
                                        <div
                                            className={`absolute-center size-3.5 bg-blue-dark transition-opacity ${
                                                isSelected
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            }`}
                                        ></div>
                                    </div>
                                )}
                                <div className="overflow-hidden text-ellipsis text-sm xl:text-[15px]">
                                    {optionString}
                                </div>
                            </ListboxOption>
                        );
                    })}
                </ListboxOptions>
            </Listbox>
        </div>
    );
}
